import { useAuth } from "../contexts/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  House,
  Motorcycle,
  QrCode,
  ClockCounterClockwise,
  SignOut,
  CaretLeft,
  User,
  ShieldStar,
  Gear,
  Wrench,
} from "@phosphor-icons/react";

export default function Layout({
  children,
  title,
  showBack = false,
  backTo = null,
}) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperadmin = user?.role === "superadmin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const navItems = isSuperadmin
    ? [
        { path: "/dashboard", icon: House, label: "Início" },
        { path: "/admin", icon: ShieldStar, label: "Empresas" },
      ]
    : [
        { path: "/dashboard", icon: House, label: "Início" },
        { path: "/motos", icon: Motorcycle, label: "Motos" },
        { path: "/consulta", icon: QrCode, label: "Consulta" },
        { path: "/historico", icon: ClockCounterClockwise, label: "Histórico" },
        { path: "/revisoes", icon: Wrench, label: "Revisões" },
      ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A] border-b border-white/10">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14">
          {/* Left Side: Back + Title */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {showBack && (
              <button
                onClick={handleBack}
                className="w-10 h-10 flex items-center justify-center -ml-2 shrink-0 rounded-lg hover:bg-[#121212] transition-colors"
                data-testid="back-button"
              >
                <CaretLeft size={24} className="text-white" />
              </button>
            )}
            {user?.company_logo && (
              <img
                src={user.company_logo}
                alt="Logo"
                className="h-8 w-8 object-contain rounded shrink-0 hidden sm:block"
              />
            )}
            <h1 className="text-base sm:text-lg font-bold truncate pr-2">
              {title}
            </h1>
          </div>

          {/* Right Side: Actions */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* User Pill - Hides on very small screens to save space */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#121212]">
              {isSuperadmin ? (
                <ShieldStar size={18} className="text-[#00E676]" />
              ) : (
                <User size={18} className="text-[#A1A1AA]" />
              )}
              <span className="text-sm text-[#A1A1AA] max-w-[100px] lg:max-w-[150px] truncate">
                {user?.name}
              </span>
            </div>

            {!isSuperadmin && (
              <Link
                to="/configuracoes"
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#121212] transition-colors"
                data-testid="settings-button"
              >
                <Gear size={22} className="text-[#A1A1AA]" />
              </Link>
            )}

            <button
              onClick={handleLogout}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#121212] transition-colors"
              data-testid="logout-button"
            >
              <SignOut size={22} className="text-[#A1A1AA]" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 safe-bottom">{children}</main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <div className="flex items-center justify-around">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== "/dashboard" &&
                location.pathname.startsWith(item.path));
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 py-2 px-2 sm:px-4 rounded-lg transition-colors ${
                  isActive ? "text-[#00E676]" : "text-[#A1A1AA]"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={24} weight={isActive ? "fill" : "regular"} />
                <span className="text-xs">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
